import {
  Add,
  AttachFile,
  Close,
  Comment,
  ReceiptLongOutlined,
  PaymentOutlined,
  HistoryOutlined,
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { offlinePaymentChannels, userCats } from '../../../../helpers/appstate';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Empty, Upload } from 'antd';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { baseURL, getPayments, getToken } from '../../../../helpers/api';
import MUIDataTable from 'mui-datatables';
import { openNotification } from '../../../../helpers/notification';
import PaymentAcknowledgement from './PaymentAcknowledgement';

export default function FacilityPaymentOffline({ props, onClose, invoiceItem, open }) {
  const { userCategory } = props;

  const [paymentList, setPaymentList] = useState([]);
  const [paymentFetching, setPaymentFetching] = useState(false);
  const [paymentSelected, setPaymentSelected] = useState();
  const [paymentRefetch, setPaymentRefetch] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [sendCert, setSendCert] = useState(false);

  useEffect(() => {
    fetchPaymentList();
  }, []);

  useEffect(() => {
    if (paymentRefetch) fetchPaymentList();
  }, [paymentRefetch]);

  // Core Formik Logic - Unchanged
  const formik = useFormik({
    initialValues: {
      paymentMethod: offlinePaymentChannels[0]?.value,
      bankName: '',
      bankAccount: '',
      paymentReference: '',
      amountPaid: '',
      paymentDescription: '',
      datePaid: new Date().toISOString().split('T')[0],
      invoiceId: '',
    },
    validationSchema: Yup.object({
      paymentMethod: Yup.string().required('Payment method is required'),
      amountPaid: Yup.string().required('Amount is required'),
    }),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleFileChange = ({ fileList }) => setFileList(fileList);

  // Core Submission Logic - Unchanged
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('invoiceId', invoiceItem?.invoiceId);
      formData.append('paymentMethod', values.paymentMethod);
      formData.append('amountPaid', values.amountPaid);
      formData.append('datePaid', values?.datePaid);
      formData.append('bankName', values.bankName);
      formData.append('bankAccount', values.bankAccount);
      formData.append('paymentReference', values.paymentReference);
      formData.append('paymentDescription', values.paymentDescription);
      formData.append('sendCertificate', sendCert);

      if (fileList?.length > 0) {
        const file = fileList[0].originFileObj;
        formData.append('receipt', file);
      }

      const res = await axios.post(`${baseURL}/Payment/SubmitPayment`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `bearer ${getToken()}`,
        },
      });
      const { status } = res?.data?.result;
      setPaymentRefetch(true);
      if (status?.code === '00') {
        openNotification({ type: 'success', message: status?.desc });
        formik.resetForm();
        setFileList([]);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Something went wrong. Please try again' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentList = async () => {
    setPaymentFetching(true);
    try {
      const res = await getPayments(
        invoiceItem?.facilityId,
        '',
        invoiceItem?.invoiceId,
        '',
        '',
        '',
        '',
        1,
        50
      );
      const { data, status } = res?.result;
      if (status?.code === '00') setPaymentList(data);
    } catch (error) {
      openNotification({ type: 'error', message: 'Unable to fetch payments' });
    } finally {
      setPaymentFetching(false);
      setPaymentRefetch(false);
    }
  };

  const renderForm = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        //bgcolor: alpha('#f9fafb', 0.5),
      }}
    >
      <Typography
        variant="body2"
        component={'div'}
        fontWeight={700}
        sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Add fontSize="small" color="primary" /> Add New Payment
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel>Payment Method</InputLabel>
            <Select
              name="paymentMethod"
              label="Payment Method"
              value={formik.values.paymentMethod}
              onChange={formik.handleChange}
              sx={{ borderRadius: '10px', bgcolor: '#fff' }}
            >
              {offlinePaymentChannels?.map((item) => (
                <MenuItem key={item?.value} value={item?.value}>
                  {item?.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Grid2 container spacing={2}>
            <Grid2 size={6}>
              <TextField
                name="amountPaid"
                label="Amount Paid"
                type="number"
                fullWidth
                size="small"
                onChange={formik.handleChange}
                value={formik.values.amountPaid}
                InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                name="datePaid"
                label="Date Paid"
                type="date"
                fullWidth
                size="small"
                onChange={formik.handleChange}
                value={formik.values.datePaid}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
              />
            </Grid2>
          </Grid2>

          <TextField
            name="bankName"
            label="Bank Name"
            fullWidth
            size="small"
            onChange={formik.handleChange}
            value={formik.values.bankName}
            disabled={formik.values.paymentMethod === 'CASH'}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
          />

          <TextField
            name="paymentReference"
            label="Reference No."
            fullWidth
            size="small"
            onChange={formik.handleChange}
            value={formik.values.paymentReference}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
          />

          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ mb: 1, display: 'block' }}
            >
              PAYMENT RECEIPT
            </Typography>
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleFileChange}
              beforeUpload={() => false}
              maxCount={1}
            >
              {fileList.length < 1 && (
                <Stack alignItems="center" spacing={0.5}>
                  <Add />
                  <Typography variant="caption" fontWeight={600}>
                    Upload
                  </Typography>
                </Stack>
              )}
            </Upload>
          </Box>

          <TextField
            name="paymentDescription"
            label="Comments"
            multiline
            rows={3}
            fullWidth
            onChange={formik.handleChange}
            value={formik.values.paymentDescription}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }}
          />

          {userCategory === userCats.EDM && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendCert}
                  onChange={(e) => setSendCert(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="caption" fontWeight={600} color="warning.main">
                  Auto-send certificate on completion
                </Typography>
              }
            />
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : <PaymentOutlined />
            }
            sx={{ py: 1.2, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
          >
            Submit Payment
          </Button>
        </Stack>
      </form>
    </Paper>
  );

  const renderPaymentListTable = () => {
    const columns = [
      {
        name: 'paymentDate',
        label: 'Paid Date',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Typography variant="body2" fontWeight={600}>
              {new Date(paymentList[index]?.paymentDate).toLocaleDateString('en-NG', {
                dateStyle: 'medium',
              })}
            </Typography>
          ),
        },
      },
      {
        name: 'amountPaid',
        label: 'Paid (₦)',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={700} color="success.main">
                {Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(
                  paymentList[index]?.amountPaid
                )}
              </Typography>
              {paymentList[index]?.paymentReceipt && (
                <IconButton
                  size="small"
                  onClick={() => window.open(paymentList[index].paymentReceipt)}
                >
                  <AttachFile sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Stack>
          ),
        },
      },
      {
        name: 'approver',
        label: 'Approver',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {paymentList[index]?.approver || 'System'}
              </Typography>
              {paymentList[index]?.approverComment && (
                <Tooltip title={paymentList[index].approverComment}>
                  <Comment sx={{ fontSize: 14, color: 'info.main', ml: 0.5 }} />
                </Tooltip>
              )}
            </Box>
          ),
        },
      },
    ];

    if (userCategory === userCats.EDM) {
      columns.push({
        name: 'ack',
        label: 'Ack.',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Button
              size="small"
              variant="outlined"
              color={paymentList[index]?.emailSent ? 'success' : 'info'}
              sx={{
                fontSize: '0.65rem',
                textTransform: 'none',
                borderRadius: '6px',
                fontWeight: 700,
              }}
              onClick={() => {
                setPaymentSelected(paymentList[index]);
                setOpenConfirmation(true);
              }}
            >
              {paymentList[index]?.emailSent ? 'Resend' : 'Send'}
            </Button>
          ),
        },
      });
    }

    return (
      <MUIDataTable
        data={paymentList}
        columns={columns}
        options={{
          elevation: 0,
          selectableRows: 'none',
          responsive: 'standard',
          pagination: false,
          setRowProps: (ev, idx) => ({
            style: { backgroundColor: idx % 2 === 0 ? '#fff' : alpha('#f5f7ff', 0.5) },
          }),
          setTableProps: () => ({ size: 'small' }),
        }}
      />
    );
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="lg"
      fullScreen
      PaperProps={{ sx: { bgcolor: '#F4F6F8' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2.5,
          bgcolor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <ReceiptLongOutlined color="primary" sx={{ mr: 1.5 }} />
        <Typography variant="h6" fontWeight={700}>
          Invoice Management
        </Typography>
        <IconButton onClick={onClose} sx={{ ml: 'auto', bgcolor: alpha('#000', 0.04) }}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent>
        {/* TOP SUMMARY CARD */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fff',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
            spacing={3}
          >
            <Box>
              <Typography
                variant="caption"
                color="textSecondary"
                fontWeight={700}
                sx={{ letterSpacing: 1 }}
              >
                INVOICE
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {invoiceItem?.invoiceTitle}
              </Typography>
              <Typography variant="body2" color="primary" fontWeight={600}>
                {invoiceItem?.facilityName}
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={4}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Box textAlign="center">
                <Typography variant="caption" fontWeight={700} color="textSecondary">
                  INVOICE VALUE
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(
                    invoiceItem?.totalAmount
                  )}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="caption" fontWeight={700} color="success.main">
                  TOTAL PAID
                </Typography>
                <Typography variant="body1" fontWeight={700} color="success.main">
                  {Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(
                    invoiceItem?.totalAmountPaid
                  )}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="caption" fontWeight={700} color="error.main">
                  OUTSTANDING
                </Typography>
                <Typography variant="body1" fontWeight={700} color="error.main">
                  {Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(
                    invoiceItem?.outstandingAmount
                  )}
                </Typography>
              </Box>
            </Stack>

            <Chip
              label={invoiceItem?.invoiceStatus}
              color={
                invoiceItem?.invoiceStatus === 'CLOSED'
                  ? 'success'
                  : invoiceItem?.invoiceStatus === 'OPEN'
                  ? 'error'
                  : 'warning'
              }
              sx={{ fontWeight: 700, borderRadius: '8px', minWidth: 100 }}
            />
          </Stack>
        </Paper>

        <Grid2 container spacing={3}>
          <Grid2 size={{ lg: 4, md: 5, xs: 12 }}>{renderForm()}</Grid2>

          <Grid2 size={{ lg: 8, md: 7, xs: 12 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: '#fff',
                minHeight: 400,
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha('#1976d2', 0.02),
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <HistoryOutlined fontSize="small" /> Payment History
                </Typography>
              </Box>
              {paymentFetching ? (
                <Stack alignItems="center" justifyContent="center" sx={{ height: 300 }}>
                  <CircularProgress size={30} />
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Loading records...
                  </Typography>
                </Stack>
              ) : paymentList?.length < 1 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ height: 300 }}>
                  <Empty description="No payments found" />
                </Stack>
              ) : (
                renderPaymentListTable()
              )}
            </Paper>
          </Grid2>
        </Grid2>
      </DialogContent>

      {openConfirmation && (
        <PaymentAcknowledgement
          open={openConfirmation}
          onClose={() => setOpenConfirmation(false)}
          paymentItem={paymentSelected}
        />
      )}
    </Dialog>
  );
}
